import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "./useStore";
import { useAuth } from "./useAuth";
import { getMe } from "../store/slices/authSlice";
import {
  hasSessionFetchStarted,
  isSessionSettled,
  markSessionFetchStarted,
  markSessionSettled,
} from "../utils/sessionBootstrapState";

/**
 * Re-reads the signed-in user from the server once per page load, before the
 * app shell renders anything.
 *
 * WHY THIS EXISTS. Everything the UI decides about access — the role, and which
 * modules the tenant has — travels on the user object, and that object was
 * written to localStorage at login and then never touched again. So a tenant
 * whose modules changed after someone signed in kept the old navigation on
 * every subsequent page load, indefinitely. The socket push
 * (`company:modules-changed`) only reaches sessions that are already open; it
 * cannot help a tab that loads after the change, which is the common case.
 *
 * WHY IT BLOCKS. Rendering first and correcting on arrival is not good enough
 * here: a deep link into a module that has since been switched off would mount
 * the page, fire its requests, collect a screenful of "Not found" toasts, and
 * only then redirect. Holding the shell for one request turns that into a clean
 * redirect with nothing shown in between.
 *
 * WHY IT NEVER BLOCKS FOR LONG. Two escapes, because a hung shell would be
 * worse than a stale one:
 *
 *   - a failed request releases immediately. Drivers run this app offline by
 *     design (see the PWA sync engine), and an offline driver has to keep
 *     working from their last known module set. The API guard still refuses
 *     anything disabled the moment they reconnect, so nothing is lost by
 *     trusting the cached copy meanwhile.
 *   - a request that neither resolves nor rejects releases on a timer.
 *
 * And it does not run at all straight after login, because that response was
 * already a fresh read — see `markSessionBootstrapped`.
 */

/** Upper bound on how long the shell may be held by a request that stalls. */
const RELEASE_AFTER_MS = 4000;

export const useSessionBootstrap = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth();

  // A session already confirmed this page load renders straight away, and so
  // does an anonymous one — there is no user to re-read.
  const [ready, setReady] = useState(
    () => isSessionSettled() || !isAuthenticated,
  );
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated || isSessionSettled()) {
      setReady(true);
      return;
    }

    const clearTimer = () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const release = () => {
      markSessionSettled();
      setReady(true);
      clearTimer();
    };

    if (!hasSessionFetchStarted()) {
      markSessionFetchStarted();
      // Settled either way: a rejected getMe means the request failed, not that
      // the session is invalid — the axios interceptor owns that decision and
      // signs the user out itself if that is what happened.
      dispatch(getMe()).finally(release);
    }

    timerRef.current = window.setTimeout(release, RELEASE_AFTER_MS);

    return clearTimer;
  }, [dispatch, isAuthenticated]);

  return ready;
};
